import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  getAdminUsers,
  setUserRole,
  setUserMentor,
  setUserBoard,
  setUserMentorId,
  assignTrack,
  type AdminUser,
  type ApiResult,
} from '../lib/api';
import { useTracks } from '../lib/data';

// Khalifa "manage people" view. All server strings rendered as React text.
// After every successful mutation the full list is re-fetched (no optimistic update).

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'forbidden' }
  | { phase: 'ready'; users: AdminUser[] };

export default function AdminPeople() {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [tick, setTick] = useState(0);
  const forbiddenRef = useRef(false);

  // Per-row busy tracking (uid set)
  const [busyUids, setBusyUids] = useState<Set<string>>(new Set());
  // Per-row error messages
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  // Per-row "confirm force demote" state
  const [showForce, setShowForce] = useState<Set<string>>(new Set());
  // Per-row track-assign panel expanded state
  const [trackExpandedUids, setTrackExpandedUids] = useState<Set<string>>(new Set());
  // Per-row newly-checked track ids (not yet assigned)
  const [trackNewSelections, setTrackNewSelections] = useState<Record<string, string[]>>({});
  // Per-row transient "assigned ✓" confirmation
  const [assignMsg, setAssignMsg] = useState<Record<string, string>>({});
  // Per-row assign-in-progress (separate from busyUids so other controls stay live)
  const [assigningUids, setAssigningUids] = useState<Set<string>>(new Set());
  // Track success-message timeout ids so we can cancel them on panel close / new selection.
  const msgTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const { tracks } = useTracks();
  // Map a track id → its title (fall back to the raw id for unpublished/unknown tracks).
  const trackTitle = (id: string) => tracks.find((t) => t.id === id)?.title ?? id;

  const reload = useCallback(() => {
    if (!forbiddenRef.current) setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (forbiddenRef.current) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    void (async () => {
      const res = await getAdminUsers();
      if (cancelled || forbiddenRef.current) return;
      if (res.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        setState({
          phase: 'error',
          message:
            res.status === 0
              ? 'Network error — check your connection.'
              : `Failed (HTTP ${res.status}).`,
        });
        return;
      }
      setState({
        phase: 'ready',
        users: Array.isArray(res.data?.users) ? res.data.users : [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  /** Run a mutation: set busy, clear row error, call action, reload on success. */
  const mutate = useCallback(async <T,>(
    uid: string,
    action: () => Promise<ApiResult<T>>,
  ): Promise<ApiResult<T>> => {
    setBusyUids((prev) => new Set([...prev, uid]));
    setRowErrors((prev) => {
      const n = { ...prev };
      delete n[uid];
      return n;
    });
    const res = await action();
    if (res.status === 403) {
      forbiddenRef.current = true;
      setState({ phase: 'forbidden' });
    }
    setBusyUids((prev) => {
      const n = new Set(prev);
      n.delete(uid);
      return n;
    });
    if (res.ok) setTick((t) => t + 1);
    return res;
  }, []);

  const setRowError = (uid: string, msg: string) =>
    setRowErrors((prev) => ({ ...prev, [uid]: msg }));

  const dismissRowError = (uid: string) => {
    setRowErrors((prev) => {
      const n = { ...prev };
      delete n[uid];
      return n;
    });
    setShowForce((prev) => {
      const n = new Set(prev);
      n.delete(uid);
      return n;
    });
  };

  const handleRoleToggle = async (u: AdminUser) => {
    const newRole: 'member' | 'admin' = u.role === 'admin' ? 'member' : 'admin';
    const label = newRole === 'admin' ? 'Khalifa' : 'Member';
    if (!window.confirm(`Change ${u.displayName || u.email}'s role to ${label}?`)) return;
    const res = await mutate(u.uid, () => setUserRole(u.uid, newRole));
    if (!res.ok) {
      const err = (res.data as { error?: string })?.error;
      if (err === 'last_admin') {
        setRowError(u.uid, "Can't demote the last Khalifa.");
      } else if (err === 'self_demote_needs_force') {
        setShowForce((prev) => new Set([...prev, u.uid]));
        setRowError(u.uid, 'You are demoting yourself — use Force to confirm.');
      } else {
        setRowError(u.uid, `Role change failed (${err ?? res.status}).`);
      }
    } else {
      setShowForce((prev) => {
        const n = new Set(prev);
        n.delete(u.uid);
        return n;
      });
    }
  };

  const handleForce = async (u: AdminUser) => {
    const res = await mutate(u.uid, () => setUserRole(u.uid, 'member', true));
    if (res.ok) {
      setShowForce((prev) => {
        const n = new Set(prev);
        n.delete(u.uid);
        return n;
      });
    } else {
      setRowError(
        u.uid,
        `Force demote failed (${(res.data as { error?: string })?.error ?? res.status}).`,
      );
    }
  };

  const handleMentor = async (u: AdminUser) => {
    const res = await mutate(u.uid, () => setUserMentor(u.uid, !u.isMentor));
    if (!res.ok) setRowError(u.uid, `Ustad toggle failed (${res.status}).`);
  };

  const handleBoard = async (u: AdminUser) => {
    const res = await mutate(u.uid, () => setUserBoard(u.uid, !u.showOnBoard));
    if (!res.ok) setRowError(u.uid, `Board toggle failed (${res.status}).`);
  };

  const handleMentorId = async (u: AdminUser, mentorId: string | null) => {
    setBusyUids((prev) => new Set([...prev, u.uid]));
    setRowErrors((prev) => {
      const n = { ...prev };
      delete n[u.uid];
      return n;
    });
    let promoted = false;
    try {
      if (mentorId) {
        const mentor = state.phase === 'ready' ? state.users.find((x) => x.uid === mentorId) : undefined;
        if (mentor && !mentor.isMentor) {
          const pr = await setUserMentor(mentorId, true);
          if (pr.status === 403) {
            forbiddenRef.current = true;
            setState({ phase: 'forbidden' });
            return;
          }
          if (!pr.ok) {
            setRowError(u.uid, 'Could not promote that teammate to Ustad — try again.');
            return;
          }
          promoted = true;
        }
      }
      const res = await setUserMentorId(u.uid, mentorId);
      if (res.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        const err = (res.data as { error?: string })?.error;
        const why =
          err === 'self_mentor'
            ? "someone can't be their own mentor."
            : err === 'not_a_mentor'
              ? "that teammate isn't an Ustad."
              : `mentor assign failed (${res.status}).`;
        setRowError(u.uid, promoted ? `Promoted to Ustad, but ${why} You can retry.` : `Can't assign — ${why}`);
        if (promoted) setTick((t) => t + 1);
        return;
      }
      setTick((t) => t + 1);
    } finally {
      setBusyUids((prev) => {
        const n = new Set(prev);
        n.delete(u.uid);
        return n;
      });
    }
  };

  const toggleTrackSelection = (uid: string, trackId: string) => {
    // Clear stale success/error messages when the user starts a new selection.
    clearTimeout(msgTimeoutsRef.current[uid]);
    delete msgTimeoutsRef.current[uid];
    setAssignMsg((prev) => {
      const m = { ...prev };
      delete m[uid];
      return m;
    });
    setRowErrors((prev) => {
      const m = { ...prev };
      delete m[uid];
      return m;
    });
    setTrackNewSelections((prev) => {
      const current = prev[uid] ?? [];
      const has = current.includes(trackId);
      return {
        ...prev,
        [uid]: has ? current.filter((id) => id !== trackId) : [...current, trackId],
      };
    });
  };

  const toggleTrackExpanded = (uid: string) => {
    setTrackExpandedUids((prev) => {
      const n = new Set(prev);
      if (n.has(uid)) {
        n.delete(uid);
        setTrackNewSelections((prev2) => {
          const m = { ...prev2 };
          delete m[uid];
          return m;
        });
        // Cancel any in-flight success-message timeout and clear stale messages.
        clearTimeout(msgTimeoutsRef.current[uid]);
        delete msgTimeoutsRef.current[uid];
        setAssignMsg((prev2) => {
          const m = { ...prev2 };
          delete m[uid];
          return m;
        });
        setRowErrors((prev2) => {
          const m = { ...prev2 };
          delete m[uid];
          return m;
        });
      } else {
        n.add(uid);
      }
      return n;
    });
  };

  const handleMultiAssign = async (u: AdminUser) => {
    // Filter out tracks already assigned — defensive; server is idempotent but don't rely on it.
    const alreadyAssigned = u.assignedTrackIds ?? [];
    const newTracks = (trackNewSelections[u.uid] ?? []).filter(
      (id) => !alreadyAssigned.includes(id),
    );
    if (newTracks.length === 0) return;

    setAssigningUids((prev) => new Set([...prev, u.uid]));
    setRowErrors((prev) => {
      const n = { ...prev };
      delete n[u.uid];
      return n;
    });

    let successCount = 0;
    let errorCount = 0;

    for (const trackId of newTracks) {
      const res = await assignTrack(u.uid, trackId);
      if (res.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        setAssigningUids((prev) => {
          const n = new Set(prev);
          n.delete(u.uid);
          return n;
        });
        return;
      }
      if (res.ok) successCount++;
      else errorCount++;
    }

    setAssigningUids((prev) => {
      const n = new Set(prev);
      n.delete(u.uid);
      return n;
    });

    // Always clear pending selections and refresh the assigned set, even on failure —
    // so failed tracks aren't silently left checked to be re-fired.
    setTrackNewSelections((prev) => {
      const n = { ...prev };
      delete n[u.uid];
      return n;
    });
    setTick((t) => t + 1);

    if (errorCount === 0) {
      // All succeeded — close panel and show timed success message.
      setTrackExpandedUids((prev) => {
        const n = new Set(prev);
        n.delete(u.uid);
        return n;
      });
      const msg = `${successCount} kushti${successCount > 1 ? 's' : ''} assigned ✓`;
      setAssignMsg((p) => ({ ...p, [u.uid]: msg }));
      clearTimeout(msgTimeoutsRef.current[u.uid]);
      msgTimeoutsRef.current[u.uid] = setTimeout(
        () =>
          setAssignMsg((p) => {
            const n = { ...p };
            delete n[u.uid];
            return n;
          }),
        2500,
      );
    } else {
      // Some or all failed — surface a short note; selections already cleared above.
      const failNote =
        successCount > 0
          ? `${successCount} assigned, ${errorCount} couldn't be assigned.`
          : `${errorCount} kushti${errorCount > 1 ? 's' : ''} couldn't be assigned.`;
      setRowError(u.uid, failNote);
    }
  };

  /**
   * Render the multi-select checklist panel for track assignment.
   * Called as a plain function (not a React component) so it safely closes over
   * parent state without breaking hook order.
   */
  const renderTrackPanel = (u: AdminUser) => {
    const assigned = u.assignedTrackIds ?? [];
    const unassigned = tracks.filter((t) => !assigned.includes(t.id));
    const selected = trackNewSelections[u.uid] ?? [];
    const assigning = assigningUids.has(u.uid);
    return (
      <div>
        <p className="mb-2 text-[11px] text-ink-400 dark:text-paper-500">
          Already assigned (grey) — assign-only, no unassign.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {/* Already assigned — checked + disabled */}
          {assigned.map((id) => (
            <label
              key={id}
              className="flex cursor-not-allowed select-none items-center gap-1.5 rounded-lg border border-verdant-300/60 bg-verdant-50 px-2 py-1 opacity-60 dark:border-verdant-700/40 dark:bg-verdant-900/20"
            >
              <input
                type="checkbox"
                checked
                onChange={() => {}}
                disabled
                className="h-3 w-3 accent-verdant-600"
              />
              <span className="text-[11px] font-medium text-verdant-700 dark:text-verdant-300">
                {trackTitle(id)}
              </span>
            </label>
          ))}
          {/* Unassigned — checkable */}
          {unassigned.map((t) => (
            <label
              key={t.id}
              className="flex cursor-pointer select-none items-center gap-1.5 rounded-lg border border-rule px-2 py-1 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
            >
              <input
                type="checkbox"
                checked={selected.includes(t.id)}
                onChange={() => toggleTrackSelection(u.uid, t.id)}
                disabled={assigning}
                className="h-3 w-3 accent-verdant-600"
              />
              <span className="text-[11px] text-ink-700 dark:text-paper-200">{t.title}</span>
            </label>
          ))}
          {tracks.length === 0 && (
            <span className="text-xs text-ink-400">No published kushtis yet.</span>
          )}
        </div>
        <div className="mt-2.5 flex items-center gap-2">
          {selected.length > 0 && (
            <button
              onClick={() => void handleMultiAssign(u)}
              disabled={assigning}
              className="rounded-lg bg-terracotta-500 px-3 py-1 text-xs font-semibold text-white transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {assigning ? 'Assigning…' : `Assign ${selected.length} kushti${selected.length > 1 ? 's' : ''}`}
            </button>
          )}
          <button
            onClick={() => toggleTrackExpanded(u.uid)}
            disabled={assigning}
            className="text-xs text-ink-400 hover:underline disabled:opacity-50"
          >
            {selected.length > 0 ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>
    );
  };

  const members = state.phase === 'ready' ? state.users : [];

  return (
    <div>
      {state.phase === 'forbidden' && (
        <div
          className="mt-6 rounded-xl border border-warn-500/40 bg-paper-0 p-4 text-sm dark:bg-clay-surface"
          role="alert"
        >
          <p className="font-semibold text-ink-900 dark:text-paper-50">Not authorized</p>
          <p className="mt-1 text-ink-500 dark:text-paper-300">
            This view is for Khalifas (admins) only.
          </p>
        </div>
      )}

      {state.phase === 'loading' && (
        <p className="mt-6 text-sm text-ink-400">Loading…</p>
      )}

      {state.phase === 'error' && (
        <div className="mt-6 text-sm text-terracotta-700">
          {state.message}{' '}
          <button onClick={reload} className="underline">
            Retry
          </button>
        </div>
      )}

      {state.phase === 'ready' && (
        <>
          <div className="mt-5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink-700 dark:text-paper-200">
              {state.users.length}{' '}
              {state.users.length === 1 ? 'person' : 'people'}
            </h2>
            <button
              onClick={reload}
              className="rounded-lg border border-rule px-2.5 py-1 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
            >
              Refresh
            </button>
          </div>

          {/* ─── Table view (md+): 7 compact columns that fit without horizontal scroll ─── */}
          <div className="mt-2 hidden overflow-x-auto rounded-xl border border-rule dark:border-clay-rule md:block">
            <table className="w-full text-sm">
              <thead className="bg-paper-100 text-left text-xs uppercase tracking-wide text-ink-400 dark:bg-clay-raised/40">
                <tr>
                  <th className="px-3 py-2 font-medium">Person</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="px-3 py-2 font-medium">Ustad</th>
                  <th className="px-3 py-2 font-medium">Board</th>
                  <th className="px-3 py-2 font-medium">Mentor</th>
                  <th className="px-3 py-2 font-medium">Kushtis</th>
                  <th className="px-3 py-2 text-right font-medium">Zor</th>
                </tr>
              </thead>
              <tbody>
                {state.users.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-ink-400"
                    >
                      No users yet.
                    </td>
                  </tr>
                ) : (
                  state.users.map((u) => {
                    const busy = busyUids.has(u.uid);
                    const rowErr = rowErrors[u.uid];
                    const forceNeeded = showForce.has(u.uid);
                    const assigned = u.assignedTrackIds ?? [];
                    const unassigned = tracks.filter((t) => !assigned.includes(t.id));
                    const isExpanded = trackExpandedUids.has(u.uid);

                    return (
                      <Fragment key={u.uid}>
                        <tr className="border-t border-rule dark:border-clay-rule">
                          {/* Avatar + name + email */}
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white">
                                {u.initials}
                              </span>
                              <div className="min-w-0">
                                <p className="font-medium text-ink-900 dark:text-paper-50">
                                  {u.displayName}
                                </p>
                                <p className="text-xs text-ink-400">{u.email}</p>
                              </div>
                            </div>
                          </td>

                          {/* Role chip + toggle */}
                          <td className="px-3 py-2">
                            <div className="flex flex-col gap-1">
                              <span
                                className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  u.role === 'admin'
                                    ? 'bg-terracotta-100 text-terracotta-700 dark:bg-terracotta-900/30 dark:text-terracotta-300'
                                    : 'bg-paper-100 text-ink-600 dark:bg-clay-raised dark:text-paper-300'
                                }`}
                              >
                                {u.role === 'admin' ? 'Khalifa' : 'Member'}
                              </span>
                              <button
                                onClick={() => void handleRoleToggle(u)}
                                disabled={busy}
                                className="text-left text-xs text-terracotta-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {u.role === 'admin' ? '→ Member' : '→ Khalifa'}
                              </button>
                              {forceNeeded && (
                                <button
                                  onClick={() => void handleForce(u)}
                                  disabled={busy}
                                  className="text-left text-xs font-semibold text-terracotta-700 underline disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Force demote
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Ustad toggle */}
                          <td className="px-3 py-2">
                            <button
                              onClick={() => void handleMentor(u)}
                              disabled={busy}
                              className={`rounded-lg px-2 py-0.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                u.isMentor
                                  ? 'bg-verdant-200/60 text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-300'
                                  : 'border border-rule text-ink-400 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised'
                              }`}
                            >
                              {u.isMentor ? 'Yes' : 'No'}
                            </button>
                          </td>

                          {/* Board toggle */}
                          <td className="px-3 py-2">
                            <button
                              onClick={() => void handleBoard(u)}
                              disabled={busy}
                              className={`rounded-lg px-2 py-0.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                u.showOnBoard
                                  ? 'bg-verdant-200/60 text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-300'
                                  : 'border border-rule text-ink-400 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised'
                              }`}
                            >
                              {u.showOnBoard ? 'Yes' : 'No'}
                            </button>
                          </td>

                          {/* Mentor select */}
                          <td className="px-3 py-2">
                            <select
                              value={u.mentorId ?? ''}
                              onChange={(e) =>
                                void handleMentorId(u, e.target.value || null)
                              }
                              disabled={busy}
                              className="max-w-[130px] rounded-lg border border-rule bg-paper-0 px-2 py-0.5 text-xs text-ink-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-200"
                            >
                              <option value="">— none —</option>
                              {members
                                .filter((m) => m.uid !== u.uid)
                                .map((m) => (
                                  <option key={m.uid} value={m.uid}>
                                    {m.displayName || m.email}
                                    {m.isMentor ? '' : ' (→ Ustad)'}
                                  </option>
                                ))}
                            </select>
                          </td>

                          {/* Trails — count badge + expand toggle (panel opens as next row) */}
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {assigned.length > 0 && (
                                <span className="inline-flex rounded-full bg-verdant-200/60 px-2 py-0.5 text-[11px] font-medium text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-300">
                                  {assigned.length} kushti{assigned.length > 1 ? 's' : ''}
                                </span>
                              )}
                              <button
                                onClick={() => toggleTrackExpanded(u.uid)}
                                disabled={busy || (unassigned.length === 0 && !isExpanded)}
                                className={`rounded-lg border px-2 py-0.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                  isExpanded
                                    ? 'border-verdant-400 bg-verdant-50 text-verdant-700 dark:border-verdant-600 dark:bg-verdant-900/20 dark:text-verdant-300'
                                    : 'border-rule text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised'
                                }`}
                              >
                                {unassigned.length === 0
                                  ? '— all assigned —'
                                  : isExpanded
                                    ? 'Close'
                                    : '+ Add'}
                              </button>
                            </div>
                            {assignMsg[u.uid] && (
                              <p className="mt-1 text-[11px] font-medium text-verdant-600 dark:text-verdant-300">
                                {assignMsg[u.uid]}
                              </p>
                            )}
                          </td>

                          {/* Zor */}
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-ink-700 dark:text-paper-200">
                            {u.points.allTime}
                          </td>
                        </tr>

                        {/* Track assign panel — full-width expanded row */}
                        {isExpanded && (
                          <tr className="border-t border-rule bg-paper-50 dark:border-clay-rule dark:bg-clay-raised/20">
                            <td colSpan={7} className="px-4 pb-3 pt-2">
                              {renderTrackPanel(u)}
                            </td>
                          </tr>
                        )}

                        {/* Error row */}
                        {rowErr && (
                          <tr className="border-t border-rule dark:border-clay-rule">
                            <td
                              colSpan={7}
                              className="px-3 pb-2 pt-1 text-xs text-terracotta-700 dark:text-terracotta-400"
                            >
                              {rowErr}{' '}
                              <button
                                onClick={() => dismissRowError(u.uid)}
                                className="underline"
                              >
                                Dismiss
                              </button>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ─── Card view (below md): stacked cards, no horizontal scroll ─── */}
          <div className="mt-2 space-y-3 md:hidden">
            {state.users.length === 0 ? (
              <p className="text-sm text-ink-400">No users yet.</p>
            ) : (
              state.users.map((u) => {
                const busy = busyUids.has(u.uid);
                const rowErr = rowErrors[u.uid];
                const forceNeeded = showForce.has(u.uid);
                const assigned = u.assignedTrackIds ?? [];
                const unassigned = tracks.filter((t) => !assigned.includes(t.id));
                const isExpanded = trackExpandedUids.has(u.uid);

                return (
                  <div
                    key={u.uid}
                    className="rounded-xl border border-rule bg-paper-0 p-3 dark:border-clay-rule dark:bg-clay-surface"
                  >
                    {/* Header: avatar + name/email + Zor */}
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white">
                        {u.initials}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-ink-900 dark:text-paper-50">{u.displayName}</p>
                        <p className="truncate text-xs text-ink-400">{u.email}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="tabular-nums text-sm font-semibold text-ink-700 dark:text-paper-200">
                          {u.points.allTime}
                        </p>
                        <p className="text-[10px] text-ink-400">Zor</p>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2.5">
                      {/* Role */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="w-14 shrink-0 text-[11px] text-ink-400">Role</span>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                            u.role === 'admin'
                              ? 'bg-terracotta-100 text-terracotta-700 dark:bg-terracotta-900/30 dark:text-terracotta-300'
                              : 'bg-paper-100 text-ink-600 dark:bg-clay-raised dark:text-paper-300'
                          }`}
                        >
                          {u.role === 'admin' ? 'Khalifa' : 'Member'}
                        </span>
                        <button
                          onClick={() => void handleRoleToggle(u)}
                          disabled={busy}
                          className="text-xs text-terracotta-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {u.role === 'admin' ? '→ Member' : '→ Khalifa'}
                        </button>
                        {forceNeeded && (
                          <button
                            onClick={() => void handleForce(u)}
                            disabled={busy}
                            className="text-xs font-semibold text-terracotta-700 underline disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Force
                          </button>
                        )}
                      </div>

                      {/* Ustad + Board (same row) */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="w-14 shrink-0 text-[11px] text-ink-400">Ustad</span>
                          <button
                            onClick={() => void handleMentor(u)}
                            disabled={busy}
                            className={`rounded-lg px-2 py-0.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                              u.isMentor
                                ? 'bg-verdant-200/60 text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-300'
                                : 'border border-rule text-ink-400 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised'
                            }`}
                          >
                            {u.isMentor ? 'Yes' : 'No'}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-ink-400">Board</span>
                          <button
                            onClick={() => void handleBoard(u)}
                            disabled={busy}
                            className={`rounded-lg px-2 py-0.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                              u.showOnBoard
                                ? 'bg-verdant-200/60 text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-300'
                                : 'border border-rule text-ink-400 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised'
                            }`}
                          >
                            {u.showOnBoard ? 'Yes' : 'No'}
                          </button>
                        </div>
                      </div>

                      {/* Mentor select */}
                      <div className="flex items-start gap-2">
                        <span className="w-14 shrink-0 pt-0.5 text-[11px] text-ink-400">Mentor</span>
                        <select
                          value={u.mentorId ?? ''}
                          onChange={(e) => void handleMentorId(u, e.target.value || null)}
                          disabled={busy}
                          className="flex-1 rounded-lg border border-rule bg-paper-0 px-2 py-0.5 text-xs text-ink-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-200"
                        >
                          <option value="">— none —</option>
                          {members
                            .filter((m) => m.uid !== u.uid)
                            .map((m) => (
                              <option key={m.uid} value={m.uid}>
                                {m.displayName || m.email}
                                {m.isMentor ? '' : ' (→ Ustad)'}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Trails */}
                      <div>
                        <div className="flex items-start gap-2">
                          <span className="w-14 shrink-0 pt-0.5 text-[11px] text-ink-400">Kushtis</span>
                          <div className="flex flex-1 flex-wrap items-center gap-1">
                            {assigned.map((id) => (
                              <span
                                key={id}
                                className="inline-flex rounded-full bg-verdant-200/60 px-2 py-0.5 text-[11px] font-medium text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-300"
                              >
                                {trackTitle(id)}
                              </span>
                            ))}
                            <button
                              onClick={() => toggleTrackExpanded(u.uid)}
                              disabled={busy || (unassigned.length === 0 && !isExpanded)}
                              className={`rounded-lg border px-2 py-0.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                isExpanded
                                  ? 'border-verdant-400 bg-verdant-50 text-verdant-700 dark:border-verdant-600 dark:bg-verdant-900/20 dark:text-verdant-300'
                                  : 'border-rule text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised'
                              }`}
                            >
                              {unassigned.length === 0
                                ? '— all assigned —'
                                : isExpanded
                                  ? 'Close'
                                  : '+ Add'}
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="mt-2 rounded-lg bg-paper-50 p-2.5 dark:bg-clay-raised/20">
                            {renderTrackPanel(u)}
                          </div>
                        )}
                        {assignMsg[u.uid] && (
                          <p className="mt-1 text-[11px] font-medium text-verdant-600 dark:text-verdant-300">
                            {assignMsg[u.uid]}
                          </p>
                        )}
                      </div>

                      {/* Row error */}
                      {rowErr && (
                        <p className="text-xs text-terracotta-700 dark:text-terracotta-400">
                          {rowErr}{' '}
                          <button onClick={() => dismissRowError(u.uid)} className="underline">
                            Dismiss
                          </button>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
